import {
  ChevronLeft,
  Edit,
  History,
  Image as ImageIcon,
  PackageMinus,
  PackagePlus,
  PackageSearch,
  Search,
  Send,
  Trash2,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";
import { toast } from "sonner";

import styles from "./Estoque.module.css";
import { MovimentacaoEstoque, Produto } from "../../types/types";

const PROJETO_OPTIONS = [
  "MONITORA",
  "CANAL DE INSPEÇÃO",
  "INSPECIONA",
  "TODOS",
];
const RAIOX_OPTIONS = [
  "TODOS",
  "SMITHS",
  "RAPISCAN",
  "VMI USB",
  "EM ABERTO",
  "NUCTECH 1.3",
  "NUCTECH 1.2",
];

const EMPTY_PRODUTO: Produto = {
  id: 0,
  nome: "",
  quantidade: 0,
  quantidadeMinima: 0,
  quantidadeMaxima: 0,
  fornecedor: "",
  especificacoes: "",
  projeto: "",
  serveParaRaioX: "",
  imagemUrl: "",
  localizacao: "",
  ultimoUsuario: "",
  observacao: "",
};

type StatusKey =
  | "Sem Estoque"
  | "Crítico"
  | "Pouco Estoque"
  | "Em Estoque"
  | "Estoque Alto";

const STATUS_STYLE: Record<StatusKey, { badge: string; gauge: string }> = {
  "Sem Estoque": {
    badge: styles.statusSemEstoque,
    gauge: "var(--status-empty)",
  },
  Crítico: { badge: styles.statusCritico, gauge: "var(--status-critical)" },
  "Pouco Estoque": { badge: styles.statusPouco, gauge: "var(--status-low)" },
  "Em Estoque": { badge: styles.statusEm, gauge: "var(--status-ok)" },
  "Estoque Alto": { badge: styles.statusAlto, gauge: "var(--status-high)" },
};

const Estoque = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);

  const onLoad = useCallback(async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API}/api/Estoque/GetAllProdutos`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (res.ok) {
      const data = await res.json();
      setProdutos(data);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/area-do-cliente");
      return;
    }

    document.title = "Íkone | Estoque Geral";

    onLoad();

    return () => {};
  }, [dispatch, navigate, sessionExpiration, token, onLoad]);

  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [produtoAtual, setProdutoAtual] = useState<Produto>(EMPTY_PRODUTO);

  const [pesquisarShown, setPesquisarShown] = useState(false);
  const [addProductoShown, setAddProductShown] = useState(false);
  const [removeProductoShown, setRemoveProductShown] = useState(false);
  const [historico, setHistorico] = useState<MovimentacaoEstoque[]>([]);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [modalShown, setModalShown] = useState(false);
  const [modalEditShown, setModalEditShown] = useState(false);
  const [quantidade, setQuantidade] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [formData, setFormData] = useState<Produto>({
    ...EMPTY_PRODUTO,
    ultimoUsuario: user?.login || "",
  });

  const [projeto, setProjeto] = useState<string>("");
  const [fornecedor, setFornecedor] = useState("");
  const [imagemModalShown, setImagemModalShown] = useState(false);
  const [imagemSelecionada, setImagemSelecionada] = useState<{
    url: string;
    nome: string;
  } | null>(null);
  const [localizacao, setLocalizacao] = useState("");
  const [status, setStatus] = useState("");

  const projetos = Array.from(new Set(produtos.map((p) => p.projeto)));
  const localizacoes = Array.from(new Set(produtos.map((p) => p.localizacao)));
  const fornecedores = Array.from(new Set(produtos.map((p) => p.fornecedor)));

  const getStatus = (
    quantidade: number,
    min: number,
    max: number,
  ): StatusKey => {
    if (quantidade === 0) return "Sem Estoque";

    const percentual = (quantidade / max) * 100;
    const percentualMin = (min / max) * 100;

    if (percentual <= percentualMin) return "Crítico";
    if (percentual > percentualMin && percentual <= 50) return "Pouco Estoque";
    if (percentual > 50 && percentual <= 100) return "Em Estoque";
    return "Estoque Alto";
  };

  const produtosFiltrados = produtos.filter((item) => {
    const nomeMatches = item.nome
      .toLowerCase()
      .includes(termoPesquisa.toLowerCase());
    const projetoMatches = projeto === "" || item.projeto === projeto;
    const fornecedorMatches =
      fornecedor === "" || item.fornecedor === fornecedor;
    const localizacaoMatches =
      localizacao === "" || item.localizacao === localizacao;
    const statusMatches =
      status === "" ||
      getStatus(
        item.quantidade,
        item.quantidadeMinima,
        item.quantidadeMaxima,
      ) === status;

    return (
      nomeMatches &&
      projetoMatches &&
      fornecedorMatches &&
      localizacaoMatches &&
      statusMatches
    );
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, type, value } = e.target;

    const newValue =
      type === "checkbox" && "checked" in e.target
        ? (e.target as HTMLInputElement).checked
        : value;

    if (modalEditShown) {
      setProdutoAtual((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  const handleOpenImagem = (item: Produto) => {
    if (!item.imagemUrl) return;
    setImagemSelecionada({ url: item.imagemUrl, nome: item.nome });
    setImagemModalShown(true);
  };

  const handleCloseImagem = () => {
    setImagemModalShown(false);
    setImagemSelecionada(null);
  };

  const handleEdit = async (produto: Produto) => {
    setProdutoAtual(produto);
    setModalEditShown(true);
    setHistoricoLoading(true);
    setHistorico([]);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/Estoque/${produto.id}/historico`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("Falha ao buscar o histórico.");

      const historicoData: MovimentacaoEstoque[] = await res.json();
      setHistorico(historicoData);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar o histórico.");
    } finally {
      setHistoricoLoading(false);
    }
  };

  const onSubmit = async (acao: string) => {
    if (acao === "Salvar") {
      try {
        const form = new FormData();

        for (const key in formData) {
          form.append(key, (formData as any)[key]);
        }

        if (file) {
          form.append("imagem", file);
        }

        const res = await fetch(`${process.env.REACT_APP_API}/api/Estoque`, {
          method: "POST",
          body: form,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          setFormData({ ...EMPTY_PRODUTO, ultimoUsuario: user?.login || "" });
          setFile(null);
          setModalShown(false);
          onLoad();
        } else {
          toast.error("Erro ao cadastrar produto.");
        }
      } catch (err) {
        console.error(err);
      }
    } else if (acao === "Editar") {
      try {
        const formData = new FormData();

        formData.append("Nome", produtoAtual.nome);
        formData.append("Quantidade", String(produtoAtual.quantidade));
        formData.append(
          "QuantidadeMinima",
          String(produtoAtual.quantidadeMinima),
        );
        formData.append(
          "QuantidadeMaxima",
          String(produtoAtual.quantidadeMaxima),
        );
        formData.append("Fornecedor", produtoAtual.fornecedor);
        formData.append("Especificacoes", produtoAtual.especificacoes);
        formData.append("Projeto", produtoAtual.projeto);
        formData.append("ServeParaRaioX", produtoAtual.serveParaRaioX);
        formData.append("Localizacao", produtoAtual.localizacao);
        formData.append("UltimoUsuario", user?.login || "");
        formData.append("Observacao", produtoAtual.observacao);

        if (file) {
          formData.append("Imagem", file);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API}/api/Estoque/${produtoAtual.id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error("Erro ao atualizar o produto.");
        }

        await response.json();
        toast.success("Produto atualizado com sucesso.");
        setFormData({ ...EMPTY_PRODUTO, ultimoUsuario: user?.login || "" });
        setFile(null);
        onLoad();
        setModalEditShown(false);
      } catch (error) {
        console.error(error);
      }
    } else if (acao === "Adicionar") {
      const dadosParaEnviar = {
        quantidade,
        ultimoUsuario: user?.login || "",
      };
      await fetch(
        `${process.env.REACT_APP_API}/api/Estoque/${produtoAtual.id}/adicionar`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },

          body: JSON.stringify(dadosParaEnviar),
        },
      )
        .then((res) => {
          if (res.ok) {
            toast.success("Produto adicionado com sucesso.");
            onLoad();
          } else {
            toast.error("Erro ao adicionar produto.");
          }
        })
        .catch((err) => console.error(err));
      setQuantidade(0);
      setAddProductShown(false);
      setModalEditShown(false);
    } else if (acao === "Retirar") {
      if (produtoAtual.quantidade - quantidade < 0) {
        toast.error("Quantidade insuficiente no estoque.");
        return;
      }
      const dadosParaEnviar = {
        quantidade,
        ultimoUsuario: user?.login || "",
      };
      await fetch(
        `${process.env.REACT_APP_API}/api/Estoque/${produtoAtual.id}/remover`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(dadosParaEnviar),
        },
      )
        .then((res) => {
          if (res.ok) {
            toast.success("Produto retirado com sucesso.");
            onLoad();
          } else {
            toast.error("Erro ao retirar produto.");
          }
        })
        .catch((err) => console.error(err));
      setQuantidade(0);
      setRemoveProductShown(false);
      setModalEditShown(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Deseja excluir o produto?")) {
      await fetch(`${process.env.REACT_APP_API}/api/Estoque/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (res.ok) {
            toast.success("Produto excluído com sucesso.");
            onLoad();
          } else {
            toast.error("Erro ao excluir produto.");
          }
        })
        .catch((err) => console.error(err));
    }
  };

  const handleSaveFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      toast.error("Nenhum arquivo selecionado.");
      return;
    }

    setFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);

    const files = event.dataTransfer.files;
    if (files.length > 0 && fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(files[0]);
      fileInputRef.current.files = dataTransfer.files;
      handleSaveFile({
        target: fileInputRef.current,
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
  };

  const resetProdutoAtual = () => {
    setProdutoAtual(EMPTY_PRODUTO);
    setModalEditShown(false);
  };

  // Signature element: a stock-level gauge showing where the current
  // quantity sits between the configured minimum and maximum thresholds.
  const renderGauge = (item: Produto, statusKey: StatusKey) => {
    const max = item.quantidadeMaxima || 1;
    const fillPct = Math.min(100, Math.max(0, (item.quantidade / max) * 100));
    const minPct = Math.min(
      100,
      Math.max(0, (item.quantidadeMinima / max) * 100),
    );
    const color = STATUS_STYLE[statusKey].gauge;

    return (
      <div className={styles.gaugeCell}>
        <span className={`${styles.mono} ${styles.cellQty}`}>
          {item.quantidade}
        </span>
        <div className={styles.gaugeTrack}>
          <div
            className={styles.gaugeFill}
            style={{ width: `${fillPct}%`, background: color }}
          />
          <div className={styles.gaugeMarker} style={{ left: `${minPct}%` }} />
        </div>
      </div>
    );
  };

  const renderHistorico = () => {
    if (historicoLoading) {
      return <p className={styles.historyEmpty}>Carregando histórico...</p>;
    }

    if (historico.length === 0) {
      return (
        <p className={styles.historyEmpty}>Nenhuma movimentação encontrada.</p>
      );
    }

    return (
      <div className={styles.historyList}>
        {historico.map((mov) => {
          const isEntrada = mov.tipoMovimentacao
            ?.toUpperCase()
            .includes("ENTRADA");
          const dataFormatada = new Date(
            mov.dataHoraMovimentacao,
          ).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });

          return (
            <div className={styles.historyItem} key={mov.id}>
              <span
                className={`${styles.historyIcon} ${
                  isEntrada ? styles.historyIconIn : styles.historyIconOut
                }`}
              >
                {isEntrada ? (
                  <PackagePlus size={14} />
                ) : (
                  <PackageMinus size={14} />
                )}
              </span>
              <div className={styles.historyBody}>
                <div className={styles.historyTopRow}>
                  <span className={styles.historyType}>
                    {mov.tipoMovimentacao} de {mov.quantidade}
                  </span>
                  <span className={styles.historyDate}>{dataFormatada}</span>
                </div>
                <div className={styles.historyMeta}>
                  <span className={styles.historyUser}>
                    por {mov.usuarioResponsavel}
                  </span>
                  <span className={styles.historyDelta}>
                    {mov.quantidadeAnterior} → {mov.quantidadePosterior}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderItemRow = (item: Produto) => {
    const statusKey = getStatus(
      item.quantidade,
      item.quantidadeMinima,
      item.quantidadeMaxima,
    );
    const statusStyle = STATUS_STYLE[statusKey];

    return (
      <tr key={item.id}>
        <td data-label="Última edição" className={styles.cellUser}>
          {item.ultimoUsuario}
        </td>
        <td data-label="Item" className={styles.cellName} title={item.nome}>
          {item.nome}
        </td>
        <td data-label="Estoque">{renderGauge(item, statusKey)}</td>
        <td data-label="Fornecedor" title={item.fornecedor}>
          {item.fornecedor}
        </td>
        <td data-label="Especificações" title={item.especificacoes}>
          {item.especificacoes}
        </td>
        <td data-label="Status">
          <span className={`${styles.badge} ${statusStyle.badge}`}>
            <span className={styles.badgeDot} />
            {statusKey}
          </span>
        </td>
        <td data-label="Projeto" title={item.projeto}>
          {item.projeto}
        </td>
        <td data-label="Raio-X">{item.serveParaRaioX}</td>
        <td data-label="Imagem">
          {item.imagemUrl ? (
            <button
              type="button"
              className={styles.thumbBtn}
              onClick={() => handleOpenImagem(item)}
              aria-label={`Ver imagem de ${item.nome}`}
            >
              <img
                src={process.env.REACT_APP_API + item.imagemUrl}
                alt={item.nome}
                className={styles.thumb}
              />
            </button>
          ) : (
            <span className={styles.thumbEmpty}>
              <ImageIcon size={16} />
            </span>
          )}
        </td>

        <td data-label="Localização" title={item.localizacao}>
          {item.localizacao}
        </td>
        <td data-label="Ações">
          <div className={styles.rowActions}>
            <button
              type="button"
              className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
              onClick={() => handleEdit(item)}
              aria-label="Editar item"
            >
              <Edit size={16} />
            </button>
            <button
              type="button"
              className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
              onClick={() => handleDelete(item.id)}
              aria-label="Excluir item"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const listaExibida = pesquisarShown ? produtosFiltrados : produtos;

  const renderUploadField = () => (
    <div className={styles.field}>
      <label htmlFor="fileInput" className={styles.label}>
        Imagem do item
      </label>

      {file ? (
        <p className={styles.fileChosen}>{file.name}</p>
      ) : (
        <>
          <div
            className={`${styles.uploadBox} ${dragActive ? styles.uploadBoxDragover : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className={styles.uploadIcon}>
              <ImageIcon size={32} />
            </div>
            <h4>Arraste e solte</h4>
            <p>Escolha somente JPG, JPEG ou PNG</p>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Escolher imagem
            </button>
          </div>

          <input
            type="file"
            id="fileInput"
            name="imagemUrl"
            accept=".jpg,.jpeg,.png"
            onChange={handleSaveFile}
            ref={fileInputRef}
            style={{ display: "none" }}
          />
        </>
      )}
    </div>
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate(-1)}
            aria-label="Voltar"
          >
            <ChevronLeft size={18} />
          </button>
          <h1 className={styles.title}>Estoque Geral</h1>
        </div>
        <p className={styles.subtitle}>
          Itens necessários para a montagem de cada projeto da Íkone e a
          quantidade disponível de cada um em estoque.
        </p>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.toolbarRow}>
          {pesquisarShown && (
            <div className={styles.searchWrap}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Pesquisar por nome do item..."
                className={styles.searchInput}
                name="pesquisa"
                autoFocus
                onChange={(ev) => setTermoPesquisa(ev.target.value)}
              />
            </div>
          )}
          {!pesquisarShown && <div className={styles.spacer} />}

          <div className={styles.actionsGroup}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => setModalShown(true)}
            >
              <PackagePlus size={16} />
              Novo item
            </button>
            <button
              type="button"
              className={`${styles.btn} ${
                pesquisarShown ? styles.btnGhostActive : styles.btnGhost
              }`}
              onClick={() => {
                setTermoPesquisa("");
                setPesquisarShown(!pesquisarShown);
              }}
            >
              {pesquisarShown ? <X size={16} /> : <Search size={16} />}
              Pesquisar
            </button>
          </div>
        </div>

        <div className={styles.filters}>
          <select
            className={styles.select}
            value={projeto}
            onChange={(ev) => setProjeto(ev.target.value)}
          >
            <option value="">Todos os projetos</option>
            {projetos.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={status}
            onChange={(ev) => setStatus(ev.target.value)}
          >
            <option value="">Todos os status</option>
            <option value="Sem Estoque">Sem Estoque</option>
            <option value="Crítico">Crítico</option>
            <option value="Pouco Estoque">Pouco Estoque</option>
            <option value="Em Estoque">Em Estoque</option>
            <option value="Estoque Alto">Estoque Alto</option>
          </select>
          <select
            className={styles.select}
            value={fornecedor}
            onChange={(ev) => setFornecedor(ev.target.value)}
          >
            <option value="">Todos os fornecedores</option>
            {fornecedores.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={localizacao}
            onChange={(ev) => setLocalizacao(ev.target.value)}
          >
            <option value="">Todas as localizações</option>
            {localizacoes.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Última edição</th>
              <th>Item</th>
              <th>Estoque</th>
              <th>Fornecedor</th>
              <th>Especificações</th>
              <th>Status</th>
              <th>Projeto</th>
              <th>Raio-X</th>
              <th>Imagem</th>
              <th>Localização</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {listaExibida.length > 0 ? (
              listaExibida.map(renderItemRow)
            ) : (
              <tr>
                <td colSpan={11}>
                  <div className={styles.emptyState}>
                    <PackageSearch size={32} />
                    <p>Nenhum item encontrado.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Cadastrar produto */}
      <Modal
        show={modalShown}
        onHide={() => setModalShown(false)}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <PackagePlus size={20} />
              Cadastrar produto
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="nome" className={styles.label}>
                Nome do item
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                className={styles.input}
                value={formData.nome}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label htmlFor="quantidade" className={styles.label}>
                  Quantidade
                </label>
                <input
                  type="number"
                  id="quantidade"
                  name="quantidade"
                  className={styles.input}
                  value={formData.quantidade}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="fornecedor" className={styles.label}>
                  Fornecedor
                </label>
                <input
                  type="text"
                  id="fornecedor"
                  name="fornecedor"
                  className={styles.input}
                  value={formData.fornecedor}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="quantidadeMinima" className={styles.label}>
                  Quantidade mínima
                </label>
                <input
                  type="number"
                  id="quantidadeMinima"
                  name="quantidadeMinima"
                  className={styles.input}
                  value={formData.quantidadeMinima}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="quantidadeMaxima" className={styles.label}>
                  Quantidade máxima
                </label>
                <input
                  type="number"
                  id="quantidadeMaxima"
                  name="quantidadeMaxima"
                  className={styles.input}
                  value={formData.quantidadeMaxima}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="especificacoes" className={styles.label}>
                Especificações
              </label>
              <textarea
                id="especificacoes"
                className={styles.textarea}
                name="especificacoes"
                value={formData.especificacoes}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label htmlFor="projeto" className={styles.label}>
                  Projeto
                </label>
                <select
                  id="projeto"
                  name="projeto"
                  className={styles.selectField}
                  value={formData.projeto}
                  onChange={handleChange}
                >
                  <option value="">Selecione o projeto</option>
                  {PROJETO_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label htmlFor="serveParaRaioX" className={styles.label}>
                  Serve para Raio-X
                </label>
                <select
                  id="serveParaRaioX"
                  name="serveParaRaioX"
                  className={styles.selectField}
                  value={formData.serveParaRaioX}
                  onChange={handleChange}
                >
                  <option value="">Selecione uma opção</option>
                  {RAIOX_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {renderUploadField()}

            <div className={styles.field}>
              <label htmlFor="localizacao" className={styles.label}>
                Localização
              </label>
              <input
                type="text"
                id="localizacao"
                name="localizacao"
                className={styles.input}
                value={formData.localizacao}
                onChange={handleChange}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setModalShown(false)}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => onSubmit("Salvar")}
          >
            <Send size={16} />
            Enviar
          </button>
        </Modal.Footer>
      </Modal>

      {/* Visualizar imagem */}
      <Modal
        show={imagemModalShown}
        onHide={handleCloseImagem}
        centered
        size="lg"
        contentClassName={styles.imageModalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <ImageIcon size={20} />
              {imagemSelecionada?.nome}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.imageModalBody}>
          {imagemSelecionada && (
            <img
              src={process.env.REACT_APP_API + imagemSelecionada.url}
              alt={imagemSelecionada.nome}
              className={styles.imageModalImg}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Editar produto */}
      <Modal
        show={modalEditShown}
        onHide={() => setModalEditShown(false)}
        centered
        size="lg"
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <Edit size={20} />
              Editar produto
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="nome" className={styles.label}>
                Nome do item
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                className={styles.input}
                value={produtoAtual.nome}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label htmlFor="quantidade" className={styles.label}>
                  Quantidade
                </label>
                <input
                  type="number"
                  id="quantidade"
                  name="quantidade"
                  className={styles.input}
                  value={produtoAtual.quantidade}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="fornecedor" className={styles.label}>
                  Fornecedor
                </label>
                <input
                  type="text"
                  id="fornecedor"
                  name="fornecedor"
                  className={styles.input}
                  value={produtoAtual.fornecedor}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="quantidadeMinima" className={styles.label}>
                  Quantidade mínima
                </label>
                <input
                  type="number"
                  id="quantidadeMinima"
                  name="quantidadeMinima"
                  className={styles.input}
                  value={produtoAtual.quantidadeMinima}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="quantidadeMaxima" className={styles.label}>
                  Quantidade máxima
                </label>
                <input
                  type="number"
                  id="quantidadeMaxima"
                  name="quantidadeMaxima"
                  className={styles.input}
                  value={produtoAtual.quantidadeMaxima}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="especificacoes" className={styles.label}>
                Especificações
              </label>
              <textarea
                id="especificacoes"
                className={styles.textarea}
                name="especificacoes"
                value={produtoAtual.especificacoes}
                onChange={handleChange}
              />
            </div>

            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label htmlFor="projeto" className={styles.label}>
                  Projeto
                </label>
                <select
                  id="projeto"
                  name="projeto"
                  className={styles.selectField}
                  value={produtoAtual.projeto}
                  onChange={handleChange}
                >
                  <option value="">Selecione o projeto</option>
                  {PROJETO_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label htmlFor="serveParaRaioX" className={styles.label}>
                  Serve para Raio-X
                </label>
                <select
                  id="serveParaRaioX"
                  name="serveParaRaioX"
                  className={styles.selectField}
                  value={produtoAtual.serveParaRaioX}
                  onChange={handleChange}
                >
                  <option value="">Selecione uma opção</option>
                  {RAIOX_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {renderUploadField()}

            <div className={styles.field}>
              <label htmlFor="localizacao" className={styles.label}>
                Localização
              </label>
              <input
                type="text"
                id="localizacao"
                name="localizacao"
                className={styles.input}
                value={produtoAtual.localizacao}
                onChange={handleChange}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                <History
                  size={14}
                  style={{ verticalAlign: "-2px", marginRight: "0.3rem" }}
                />
                Histórico de movimentações
              </label>
              <div className={styles.historyBox}>{renderHistorico()}</div>
            </div>

            <div className={styles.field}>
              <label htmlFor="observacao" className={styles.label}>
                Observações adicionais
              </label>
              <textarea
                id="observacao"
                name="observacao"
                className={styles.textarea}
                value={produtoAtual.observacao}
                onChange={handleChange}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={resetProdutoAtual}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnDanger}`}
            onClick={() => {
              setModalEditShown(false);
              setRemoveProductShown(true);
            }}
          >
            <PackageMinus size={16} />
            Retirar
          </button>
          <button
            className={`${styles.btn} ${styles.btnSuccess}`}
            onClick={() => {
              setModalEditShown(false);
              setAddProductShown(true);
            }}
          >
            <PackagePlus size={16} />
            Adicionar
          </button>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => onSubmit("Editar")}
          >
            <Send size={16} />
            Enviar
          </button>
        </Modal.Footer>
      </Modal>

      {/* Adicionar quantidade */}
      <Modal
        show={addProductoShown}
        onHide={() => {
          setQuantidade(0);
          setAddProductShown(false);
          setModalEditShown(true);
        }}
        centered
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <PackagePlus size={20} />
              Adicionar produto
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.field}>
            <label htmlFor="quantidade" className={styles.label}>
              Quantidade a adicionar
            </label>
            <input
              type="number"
              id="quantidade"
              name="quantidade"
              className={styles.input}
              value={quantidade}
              onChange={(e) => setQuantidade(Number(e.target.value))}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => {
              setQuantidade(0);
              setAddProductShown(false);
              setModalEditShown(true);
            }}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnSuccess}`}
            onClick={() => onSubmit("Adicionar")}
          >
            <Send size={16} />
            Enviar
          </button>
        </Modal.Footer>
      </Modal>

      {/* Retirar quantidade */}
      <Modal
        show={removeProductoShown}
        onHide={() => {
          setQuantidade(0);
          setRemoveProductShown(false);
          setModalEditShown(true);
        }}
        centered
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <span className={styles.headerTop} style={{ gap: "0.5rem" }}>
              <PackageMinus size={20} />
              Retirar produto
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.field}>
            <label htmlFor="quantidade" className={styles.label}>
              Quantidade a retirar
            </label>
            <input
              type="number"
              id="quantidade"
              name="quantidade"
              className={styles.input}
              value={quantidade}
              onChange={(e) => setQuantidade(Number(e.target.value))}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => {
              setQuantidade(0);
              setRemoveProductShown(false);
              setModalEditShown(true);
            }}
          >
            Fechar
          </button>
          <button
            className={`${styles.btn} ${styles.btnDanger}`}
            onClick={() => onSubmit("Retirar")}
          >
            <Send size={16} />
            Enviar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Estoque;
